import { FileDown, FileText } from "lucide-react";
import { getMediaUrl } from "../../services/midiaApi";

/**
 * Visualizador inline de mídia dentro de MessageBubble.
 * - image: <img> com lazy loading e link para abrir em tamanho real
 * - video: <video controls> nativo
 * - audio: <audio controls> nativo
 * - document: link de download com ícone e nome do arquivo
 *
 * O endpoint GET /contas/:id/midia/:mid é autenticado via cookie.
 * Como o frontend usa withCredentials=true e a URL é same-origin,
 * <img src> e <video src> funcionam diretamente sem fetch extra.
 *
 * @param {{
 *   tipo: 'image'|'video'|'audio'|'document',
 *   contaId: number,
 *   mid: number|string,
 *   caption?: string,
 *   filename?: string,
 * }} props
 */
export default function MediaViewer({ tipo, contaId, mid, caption, filename }) {
  const src  = getMediaUrl(contaId, mid);
  const nome = filename ?? "documento";

  if (tipo === "image") {
    return (
      <div className="flex flex-col gap-1 max-w-[280px]">
        <a
          href={src}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={caption ? `Imagem: ${caption}` : "Abrir imagem em tamanho real"}
          className="block rounded-lg overflow-hidden border border-white/[0.08] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
        >
          <img
            src={src}
            alt={caption ?? "Imagem recebida"}
            loading="lazy"
            className="w-full max-h-[240px] object-cover bg-surface-2"
          />
        </a>
        {caption && (
          <p className="text-xs text-ink-3 px-0.5 leading-snug">{caption}</p>
        )}
      </div>
    );
  }

  if (tipo === "video") {
    return (
      <div className="flex flex-col gap-1 max-w-[280px]">
        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <video
          controls
          src={src}
          aria-label={caption ?? "Vídeo recebido"}
          className="w-full max-h-[200px] rounded-lg border border-white/[0.08] bg-surface-1"
        />
        {caption && (
          <p className="text-xs text-ink-3 px-0.5 leading-snug">{caption}</p>
        )}
      </div>
    );
  }

  if (tipo === "audio") {
    return (
      <div className="w-full max-w-[280px]">
        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <audio
          controls
          src={src}
          aria-label="Áudio recebido"
          className="w-full h-9"
        />
      </div>
    );
  }

  if (tipo === "document") {
    return (
      <a
        href={src}
        download={nome}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`Baixar documento: ${nome}`}
        className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg border border-white/[0.08] bg-surface-2 hover:bg-surface-3 transition-colors max-w-[260px] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
      >
        <div
          className="w-8 h-8 rounded bg-brand-900 flex items-center justify-center flex-shrink-0"
          aria-hidden="true"
        >
          <FileText className="w-4 h-4 text-brand-300" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-ink-1 truncate leading-tight">
            {nome}
          </p>
          <p className="text-xs text-ink-4 mt-0.5 flex items-center gap-1">
            <FileDown className="w-3 h-3" aria-hidden="true" />
            Baixar
          </p>
        </div>
      </a>
    );
  }

  return null;
}
